/**
 * AdditionalInfoPanel — visual treatment for the "Additional Information
 * Needed" stage. Surfaces:
 *   • Who we're waiting on (chip)
 *   • Outstanding items (checklist)
 *   • Date requested
 *   • Days waiting (auto-computed)
 *   • Last note from staff
 *
 * Staff can edit inline (waiting_on + items + note). PM-side renders as a
 * read-only panel + an "Add response" textarea that PATCHes the same payload.
 *
 * Hooks to: PATCH /api/screenings/{sid}/additional-info
 *           DELETE /api/screenings/{sid}/additional-info  (staff only)
 */
import { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Clock, Hourglass, X, Plus, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dateFormat";

const WAITING_OPTIONS = [
  { value: "applicant",         label: "Applicant" },
  { value: "employer",          label: "Employer" },
  { value: "landlord",          label: "Landlord / Prior Landlord" },
  { value: "property_manager",  label: "Property Manager" },
  { value: "other",             label: "Other source" },
];

function daysBetween(fromIso) {
  if (!fromIso) return 0;
  const diff = (Date.now() - new Date(fromIso).getTime()) / (1000 * 60 * 60 * 24);
  return Math.max(0, Math.floor(diff));
}

export default function AdditionalInfoPanel({ screening, isStaff, onUpdate }) {
  const info = screening?.additional_info || null;
  const [editing, setEditing] = useState(false);
  const [waitingOn, setWaitingOn] = useState(info?.waiting_on || "applicant");
  const [items, setItems] = useState(info?.outstanding_items || []);
  const [draft, setDraft] = useState("");
  const [note, setNote] = useState(info?.note || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setWaitingOn(info?.waiting_on || "applicant");
    setItems(info?.outstanding_items || []);
    setNote(info?.note || "");
  }, [info]);

  const addItem = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (items.includes(trimmed)) { setDraft(""); return; }
    setItems((cur) => [...cur, trimmed]);
    setDraft("");
  };
  const removeItem = (item) => setItems((cur) => cur.filter((i) => i !== item));

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.patch(`/screenings/${screening.id}/additional-info`, {
        waiting_on: waitingOn,
        outstanding_items: items,
        note: note,
      });
      toast.success("Additional info updated");
      setEditing(false);
      onUpdate?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear the additional-info request? Staff can re-open it any time.")) return;
    setBusy(true);
    try {
      await apiClient.delete(`/screenings/${screening.id}/additional-info`);
      toast.success("Cleared — applicant is back in active processing");
      onUpdate?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not clear");
    } finally { setBusy(false); }
  };

  // Empty + staff -> "open the panel" CTA; PM -> nothing yet.
  if (!info) {
    if (!isStaff) return null;
    return (
      <div className="bg-amber-50 ring-1 ring-amber-200 rounded-md p-4 mt-4" data-testid="additional-info-panel-empty">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5" />
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-amber-700">Additional Information Needed</div>
              <div className="text-sm text-slate-700 mt-1">Open an outstanding-items list when something is blocking processing.</div>
            </div>
          </div>
          <Button size="sm" variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100"
                  onClick={() => setEditing(true)}
                  data-testid="additional-info-open-btn">
            Mark waiting
          </Button>
        </div>
        {editing && (
          <EditorForm
            waitingOn={waitingOn} setWaitingOn={setWaitingOn}
            items={items} setItems={setItems}
            draft={draft} setDraft={setDraft}
            addItem={addItem} removeItem={removeItem}
            note={note} setNote={setNote}
            onCancel={() => setEditing(false)}
            onSave={save} busy={busy}
          />
        )}
      </div>
    );
  }

  const daysWaiting = daysBetween(info.requested_at);
  const waitingLabel = WAITING_OPTIONS.find((w) => w.value === info.waiting_on)?.label || info.waiting_on || "—";

  return (
    <div className="bg-amber-50 ring-1 ring-amber-200 rounded-md p-5 mt-4" data-testid="additional-info-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-9 w-9 rounded-full bg-amber-100 ring-1 ring-amber-200 flex items-center justify-center shrink-0">
            <AlertCircle className="h-4 w-4 text-amber-700" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] tracking-widest uppercase font-bold text-amber-700">Additional Information Needed</div>
            <div className="font-serif text-lg text-slate-900 mt-0.5">
              Waiting on <span className="underline decoration-amber-400 underline-offset-4">{waitingLabel}</span>
            </div>
            <div className="text-[11px] font-mono text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Requested {info.requested_at ? fmtDate(info.requested_at) : "—"}</span>
              <span className="inline-flex items-center gap-1"><Hourglass className="h-3 w-3" /> {daysWaiting} day{daysWaiting !== 1 ? "s" : ""} waiting</span>
              {info.requested_by_name && <span>· by {info.requested_by_name}</span>}
            </div>
          </div>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase font-semibold tracking-wider bg-amber-100 text-amber-800 border-amber-300">
          {items.length} outstanding
        </Badge>
      </div>

      {/* Outstanding items list */}
      {items.length > 0 && (
        <ul className="mt-4 space-y-1.5" data-testid="additional-info-items">
          {items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-slate-800 bg-white rounded-md px-3 py-2 ring-1 ring-amber-100">
              <span className="font-mono text-[11px] text-amber-700 mt-0.5">·</span>
              <span className="flex-1 break-words">{item}</span>
            </li>
          ))}
        </ul>
      )}

      {info.note && (
        <div className="mt-3 text-sm text-slate-700 whitespace-pre-wrap break-words border-l-2 border-amber-300 pl-3 italic">
          {info.note}
        </div>
      )}

      {/* Staff edit / clear controls */}
      {isStaff && !editing && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100"
                  onClick={() => setEditing(true)}
                  data-testid="additional-info-edit-btn">
            Edit
          </Button>
          <Button size="sm" variant="outline"
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                  onClick={clear} disabled={busy}
                  data-testid="additional-info-clear-btn">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Info received — clear
          </Button>
        </div>
      )}

      {/* PM-side "add response" textarea */}
      {!isStaff && !editing && (
        <div className="mt-4 bg-white rounded-md ring-1 ring-amber-100 p-3" data-testid="additional-info-pm-respond">
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Update outstanding items</Label>
          <Textarea
            value={note} onChange={(e) => setNote(e.target.value)}
            rows={2} placeholder="Add a note to your NTN team about progress on these items…"
            className="mt-1.5 rounded-md text-sm"
            data-testid="additional-info-pm-note"
          />
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={save} disabled={busy || !note.trim()}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="additional-info-pm-submit">
              <Send className="h-3.5 w-3.5 mr-1.5" /> Send update
            </Button>
          </div>
        </div>
      )}

      {editing && (
        <EditorForm
          waitingOn={waitingOn} setWaitingOn={setWaitingOn}
          items={items} setItems={setItems}
          draft={draft} setDraft={setDraft}
          addItem={addItem} removeItem={removeItem}
          note={note} setNote={setNote}
          onCancel={() => setEditing(false)}
          onSave={save} busy={busy}
        />
      )}
    </div>
  );
}

function EditorForm({
  waitingOn, setWaitingOn, items, removeItem, draft, setDraft, addItem,
  note, setNote, onCancel, onSave, busy,
}) {
  return (
    <div className="mt-4 bg-white rounded-md ring-1 ring-amber-200 p-4 space-y-3" data-testid="additional-info-editor">
      <div>
        <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Waiting on</Label>
        <Select value={waitingOn} onValueChange={setWaitingOn}>
          <SelectTrigger className="h-10 rounded-md mt-1.5" data-testid="additional-info-waiting-on">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WAITING_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Outstanding items</Label>
        <div className="mt-1.5 flex gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                 placeholder="e.g. Recent pay stub from current employer"
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
                 className="h-10 rounded-md text-sm"
                 data-testid="additional-info-item-input" />
          <Button size="sm" type="button" onClick={addItem} className="bg-slate-900 h-10"
                  data-testid="additional-info-item-add">
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
        {items.length > 0 && (
          <ul className="mt-2 space-y-1">
            {items.map((it, i) => (
              <li key={i} className="flex items-center justify-between bg-amber-50 px-2 py-1.5 rounded-md text-xs">
                <span className="flex-1 truncate">{it}</span>
                <button type="button" onClick={() => removeItem(it)} className="text-slate-500 hover:text-red-600 ml-2">
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Note (visible to PM)</Label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                  rows={2} maxLength={1000} placeholder="Context for the PM — optional"
                  className="mt-1.5 rounded-md text-sm" data-testid="additional-info-note" />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button size="sm" onClick={onSave} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="additional-info-save">
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
